import { useState, useEffect, useRef, useCallback } from 'react'
import { websocketManager } from './websocketManager'

interface UseWebSocketReturn {
  connect: (sessionId?: string | null) => void
  disconnect: () => void
  sendMessage: (message: string) => void
  isConnected: boolean
  connectionError: string | null
}

export function useWebSocket(sessionId?: string | null): UseWebSocketReturn {
  const [isConnected, setIsConnected] = useState(false)
  const [connectionError, setConnectionError] = useState<string | null>(null)
  const lastSessionIdRef = useRef<string | null>(null)

  // Subscribe to connection changes
  useEffect(() => {
    const unsubscribeConnection = websocketManager.onConnectionChange((connected) => {
      setIsConnected(connected)
      if (!connected) {
        setConnectionError('Connection lost')
      } else {
        setConnectionError(null)
      }
    })

    const unsubscribeMessage = websocketManager.onMessage((data) => {
      // Dispatch events for components to handle
      if (data.type === 'connection') {
        window.dispatchEvent(new CustomEvent('system-message', { detail: data }))
      } else if (data.type === 'message') {
        window.dispatchEvent(new CustomEvent('chat-message', { detail: data }))
      } else if (data.type === 'system') {
        window.dispatchEvent(new CustomEvent('system-message', { detail: data }))
      }
    })

    // Set initial connection state
    setIsConnected(websocketManager.isConnected())

    return () => {
      unsubscribeConnection()
      unsubscribeMessage()
    }
  }, [])

  // Connect when session ID is available or changes
  useEffect(() => {
    // Validate session ID is a valid UUID before connecting
    const isValidUUID = (str: string | null | undefined): boolean => {
      if (!str) return false
      const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
      return uuidRegex.test(str)
    }

    // Only connect if sessionId exists, is different from last one, and is a valid UUID
    if (sessionId) {
      if (!isValidUUID(sessionId)) {
        console.error('[useWebSocket] Invalid session ID format (not a UUID), rejecting:', sessionId)
        console.error('[useWebSocket] Please ensure a valid backend session is created')
        return
      }

      if (lastSessionIdRef.current !== sessionId) {
        console.log('[useWebSocket] Session ID changed, connecting:', sessionId)
        lastSessionIdRef.current = sessionId
        // Check if already connected with this session - if so, don't reconnect
        if (websocketManager.getSessionId() !== sessionId || !websocketManager.isConnected()) {
          websocketManager.connect(sessionId)
        } else {
          console.log('[useWebSocket] Already connected with this session, skipping')
        }
      }
    } else if (!sessionId && lastSessionIdRef.current) {
      // Session cleared
      console.log('[useWebSocket] Session cleared')
      lastSessionIdRef.current = null
      // Don't disconnect - keep connection alive if it exists
    }
  }, [sessionId])

  const connect = useCallback((newSessionId?: string | null) => {
    const targetSessionId = newSessionId || sessionId
    if (targetSessionId) {
      console.log('[useWebSocket] Manual connect call with session:', targetSessionId)
      lastSessionIdRef.current = targetSessionId
      websocketManager.connect(targetSessionId)
    }
  }, [sessionId])

  const disconnect = useCallback(() => {
    console.log('[useWebSocket] Manual disconnect call')
    websocketManager.disconnect()
    lastSessionIdRef.current = null
  }, [])

  const sendMessage = useCallback((message: string) => {
    const success = websocketManager.sendMessage(message)
    if (!success) {
      setConnectionError('Not connected to server')
    }
  }, [])

  return {
    connect,
    disconnect,
    sendMessage,
    isConnected,
    connectionError,
  }
}


